import { FacebookPost } from 'src/DTO/FacebookPost.type';
import { useApi } from './useApi';
import { BaseHookRequest, BaseRequest } from './useApi.types';
import {
  FacebookPostResponse,
  FacebookPostPayload,
  UpdateFacebookPostPayload,
  UpdateScheduledFacebookPostPayload,
  ScheduleFacebookPostPayload,
  GetScheduledFacebookPostsResponse,
  FacebookPhotoUploadResponse,
  AuthFacebookPagesPayload,
  FacebookPagesDataResponse,
  FacebookGroupsDataResponse,
  FacebookGroupPayload,
} from './useFacebookPost.types';

export const useGetFacebookPosts: BaseHookRequest<FacebookPostResponse, FacebookPostPayload> =
  params => {
    const [{ getRequest }, state] = useApi<FacebookPostResponse>(params);

    const getFacebookPosts: BaseRequest<FacebookPostPayload> = details => {
      return getRequest(`/manager/facebook/posts/${details?.variables?.jobId}`);
    };

    return [getFacebookPosts, state];
  };

export const useUpdateFacebookPost: BaseHookRequest<FacebookPost, UpdateFacebookPostPayload> =
  params => {
    const [{ postRequest }, state] = useApi<FacebookPost>(params);
    const updateFacebookPost: BaseRequest<UpdateFacebookPostPayload> = details => {
      return postRequest<UpdateFacebookPostPayload>('/manager/facebook/post/update', {
        updatedFacebookPost: details?.variables.updatedFacebookPost,
      });
    };
    return [updateFacebookPost, state];
  };

export const useUpdatePostSchedule: BaseHookRequest<undefined, UpdateScheduledFacebookPostPayload> =
  params => {
    const [{ postRequest }, state] = useApi<undefined>(params);
    const updatePostSchedule: BaseRequest<UpdateScheduledFacebookPostPayload> = details => {
      return postRequest<UpdateScheduledFacebookPostPayload>(
        '/manager/facebook/post/schedule/update',
        {
          scheduledPostData: details?.variables.scheduledPostData,
        },
      );
    };
    return [updatePostSchedule, state];
  };

export const useScheduleFacebookPost: BaseHookRequest<undefined, ScheduleFacebookPostPayload> =
  params => {
    const [{ postRequest }, state] = useApi<undefined>(params);
    const scheduleFacebookPost: BaseRequest<ScheduleFacebookPostPayload> = details => {
      return postRequest<ScheduleFacebookPostPayload>('/manager/facebook/post/schedule', {
        postSchedule: details?.variables?.postSchedule,
      });
    };
    return [scheduleFacebookPost, state];
  };

export const useAuthFacebookPages: BaseHookRequest<undefined, AuthFacebookPagesPayload> =
  params => {
    const [{ postRequest }, state] = useApi<undefined>(params);
    const authFacebookPages: BaseRequest<AuthFacebookPagesPayload> = details => {
      const accessToken = details?.variables?.accessToken;
      const jobId = details?.variables?.jobId;

      if (!accessToken) {
        throw new Error('Access token not found');
      }

      if (!jobId) {
        throw new Error('Job ID not found');
      }

      return postRequest<AuthFacebookPagesPayload>('/manager/facebook/auth/pages', {
        accessToken,
        jobId,
      });
    };
    return [authFacebookPages, state];
  };

export const useGetScheduledFacebookPosts: BaseHookRequest<
  GetScheduledFacebookPostsResponse,
  null
> = params => {
  const [{ getRequest }, state] = useApi<GetScheduledFacebookPostsResponse>(params);
  const getScheduledFbPosts: BaseRequest<null> = () => {
    getRequest('/manager/facebook/post/scheduled');
  };
  return [getScheduledFbPosts, state];
};

export const useUploadFacebookPhotos: BaseHookRequest<FacebookPhotoUploadResponse, FormData> =
  params => {
    const [{ postRequest }, state] = useApi<FacebookPhotoUploadResponse>(params);

    const uploadFacebookPhotos: BaseRequest<FormData> = details => {
      const formData = details?.variables;

      if (!formData || !(formData instanceof FormData)) {
        throw new Error('FormData is invalid or missing');
      }

      return postRequest('/manager/facebook/post/photos', formData);
    };

    return [uploadFacebookPhotos, state];
  };

export const useGetConnectedFacebookPages: BaseHookRequest<FacebookPagesDataResponse, undefined> =
  params => {
    const [{ getRequest }, state] = useApi<FacebookPagesDataResponse>(params);
    const getConnectedFacebookPages: BaseRequest<undefined> = () => {
      return getRequest<undefined>('/manager/facebook/pages');
    };
    return [getConnectedFacebookPages, state];
  };

export const useGetConnectedFacebookGroups: BaseHookRequest<FacebookGroupsDataResponse, undefined> =
  params => {
    const [{ getRequest }, state] = useApi<FacebookGroupsDataResponse>(params);
    const getConnectedFacebookGroups: BaseRequest<undefined> = () => {
      return getRequest<undefined>('/manager/facebook/groups');
    };
    return [getConnectedFacebookGroups, state];
  };

export const useAddFacebookGroups: BaseHookRequest<undefined, FacebookGroupPayload> = params => {
  const [{ postRequest }, state] = useApi<undefined>(params);
  const addFacebookGroups: BaseRequest<FacebookGroupPayload> = details => {
    return postRequest<FacebookGroupPayload>('/manager/facebook/add/group', details?.variables);
  };
  return [addFacebookGroups, state];
};
